/**
 * Pure builders for the Nominal / Real / Excess grouped-bar chart.
 *
 * The same `/api/mars/forecasts/returns?view=nominal-real` pipeline is rendered in two places:
 *   - `features/dashboard/sections/ReturnsMiniSection.tsx` (2x2 teaser)
 *   - `features/mars-forecasts/NominalRealTab.tsx` (full forecasts tab)
 *
 * Both consumers used to independently implement the numeric reader, the
 * group-by-asset / sort-by-horizon step, the 10%-margin global y-range, and
 * the grouped-bar trace builder. This module owns that shared logic; each
 * consumer keeps only its own layout sizing and card shell.
 *
 * No React, no feature-state reads, no side effects — safe to unit-test
 * directly.
 */
import type { Data } from 'plotly.js';
import { hexToRgba } from './colors';
import { hoverTemplate } from './hoverTemplates';

/**
 * Minimal row contract shared by the two feature-level `NominalRealRow`
 * aliases. The dashboard copy has no index signature, so this lists the
 * read columns explicitly (mixed casing, string-or-number) rather than
 * widening to `Record<string, unknown>`.
 */
export interface NominalRealRowLike {
  readonly assetclass?: string;
  readonly AssetClass?: string;
  readonly horizon?: number | string;
  readonly Horizon?: number | string;
  readonly 'nominal total'?: number | string;
  readonly 'Nominal Total'?: number | string;
  readonly real?: number | string;
  readonly Real?: number | string;
  readonly 'excess of cash'?: number | string;
  readonly 'Excess of Cash'?: number | string;
}

/** One asset class's per-horizon Nominal / Real / Excess breakdown. */
export interface HorizonEntry {
  horizon: number;
  nominal: number;
  real: number;
  excess: number;
}

/** Read a number from a row, tolerating mixed casing and string values. */
function readNum(row: NominalRealRowLike, ...keys: (keyof NominalRealRowLike)[]): number {
  for (const k of keys) {
    const v = row[k];
    if (v === undefined || v === null || v === '') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    if (Number.isFinite(n)) return n;
  }
  return NaN;
}

/**
 * Group rows by asset class; drop rows missing any numeric field; sort each
 * bucket ascending by horizon.
 *
 * Mirrors the `groupByAsset` / `groupRows` copies byte-for-byte: same column
 * fallbacks (`assetclass`/`AssetClass`, `horizon`/`Horizon`, etc.) and the
 * same `Number.isFinite`-on-all-four filter.
 */
export function groupNominalReal(
  rows: readonly NominalRealRowLike[] | undefined,
): Map<string, HorizonEntry[]> {
  const grouped = new Map<string, HorizonEntry[]>();
  if (!rows) return grouped;

  for (const row of rows) {
    const asset = String(row.assetclass ?? row.AssetClass ?? '');
    if (!asset) continue;

    const horizon = readNum(row, 'horizon', 'Horizon');
    const nominal = readNum(row, 'nominal total', 'Nominal Total');
    const real = readNum(row, 'real', 'Real');
    const excess = readNum(row, 'excess of cash', 'Excess of Cash');
    if (![horizon, nominal, real, excess].every(Number.isFinite)) continue;

    const bucket = grouped.get(asset) ?? [];
    bucket.push({ horizon, nominal, real, excess });
    grouped.set(asset, bucket);
  }

  for (const [, entries] of grouped.entries()) {
    entries.sort((a, b) => a.horizon - b.horizon);
  }
  return grouped;
}

/**
 * Compute a single global [min, max] y-range across every value in every
 * asset bucket, padded by a 10% margin on each side. Returns `null` when
 * there are no values (callers fall back to Plotly autorange).
 *
 * Mirrors `computeGlobalYLimits` / `calculateGlobalYLimits`.
 */
export function computeNominalRealYRange(
  grouped: Map<string, HorizonEntry[]>,
): [number, number] | null {
  const allValues: number[] = [];
  for (const [, entries] of grouped.entries()) {
    for (const e of entries) {
      allValues.push(e.nominal, e.real, e.excess);
    }
  }
  if (allValues.length === 0) return null;
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = max - min;
  const margin = range * 0.1; // 10% margin
  return [min - margin, max + margin];
}

/** Options that vary between the two consumers of {@link buildNominalRealTraces}. */
export interface NominalRealTraceOptions {
  /**
   * `textfont` size on every bar's `toFixed(1)` value label.
   * ReturnsMiniSection uses 14; NominalRealTab uses 12.
   */
  textfontSize: number;
  /**
   * d3-format spec for the hover value. ReturnsMiniSection passes `'.1f'`;
   * NominalRealTab omits it (the `hoverTemplate` default `'.2f'`).
   */
  hoverFormat?: string;
  /**
   * Whether to emit the Excess bar trace. NominalRealTab always emits it;
   * ReturnsMiniSection suppresses it for cash asset cells.
   * Default `true`.
   */
  includeExcess?: boolean;
}

/**
 * Build the grouped bar traces for a single asset cell.
 *
 * Opacity gradient is fixed across both consumers: Nominal = 1.0, Real =
 * 0.5, Excess = 0.25. `textposition` is always `'outside'`; value labels are
 * `toFixed(1)`; hover names are the literal trace names.
 */
export function buildNominalRealTraces(
  entries: readonly HorizonEntry[],
  color: string,
  opts: NominalRealTraceOptions,
): Data[] {
  if (entries.length === 0) return [];
  const { textfontSize, hoverFormat, includeExcess = true } = opts;
  const xs = entries.map((e) => `${e.horizon}Y`);

  const traces: Data[] = [
    {
      type: 'bar' as const,
      name: 'Nominal',
      x: xs,
      y: entries.map((e) => e.nominal),
      marker: { color: hexToRgba(color, 1.0) },
      text: entries.map((e) => e.nominal.toFixed(1)),
      textposition: 'outside' as const,
      textfont: { size: textfontSize },
      hovertemplate: hoverTemplate({ name: 'Nominal', format: hoverFormat, suffix: '%' }),
    },
    {
      type: 'bar' as const,
      name: 'Real',
      x: xs,
      y: entries.map((e) => e.real),
      marker: { color: hexToRgba(color, 0.5) },
      text: entries.map((e) => e.real.toFixed(1)),
      textposition: 'outside' as const,
      textfont: { size: textfontSize },
      hovertemplate: hoverTemplate({ name: 'Real', format: hoverFormat, suffix: '%' }),
    },
  ];

  if (includeExcess) {
    traces.push({
      type: 'bar' as const,
      name: 'Excess',
      x: xs,
      y: entries.map((e) => e.excess),
      marker: { color: hexToRgba(color, 0.25) },
      text: entries.map((e) => e.excess.toFixed(1)),
      textposition: 'outside' as const,
      textfont: { size: textfontSize },
      hovertemplate: hoverTemplate({ name: 'Excess', format: hoverFormat, suffix: '%' }),
    });
  }

  return traces;
}
